import { Room } from 'matrix-js-sdk/src/matrix';
import { useMemo } from 'react';

import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import useIsRoomInCurrentWorkspace from 'hooks/useIsRoomInCurrentWorkspace';
import useScopedUserPowerLevels from 'hooks/useScopedUserPowerLevels/useScopedUserPowerLevels';
import {
  canBanFromMeetingForRoom,
  canEditMeetingDetailsForRoom,
  canKickFromMeetingForRoom,
  canToggleWaitingListForRoom,
} from 'utils/meetingPermissionGates';
import { getCanonicalParentRoom } from 'utils/roomHierarchyUtils';

const useMeetingPermissionsForRoom = (room: Room | null | undefined) => {
  const { matrixClient } = useMatrixContext();
  const { getIsRoomInCurrentWorkspace } = useIsRoomInCurrentWorkspace();
  const workspaceRoomId = room ? getCanonicalParentRoom(room)?.roomId ?? null : null;
  const scopedPowerLevels = useScopedUserPowerLevels(workspaceRoomId);
  const actorUserId = matrixClient.getUserId();

  return useMemo(
    () => ({
      scopedMeetingPl: scopedPowerLevels.meeting,
      canToggleWaitingList: canToggleWaitingListForRoom(
        room ?? null,
        actorUserId,
        scopedPowerLevels.meeting,
        getIsRoomInCurrentWorkspace
      ),
      canEditMeetingDetails: canEditMeetingDetailsForRoom(
        room ?? null,
        actorUserId,
        scopedPowerLevels.meeting,
        getIsRoomInCurrentWorkspace
      ),
      canKickFromMeeting: canKickFromMeetingForRoom(
        room ?? null,
        actorUserId,
        scopedPowerLevels.meeting,
        getIsRoomInCurrentWorkspace
      ),
      canBanFromMeeting: canBanFromMeetingForRoom(
        room ?? null,
        actorUserId,
        scopedPowerLevels.meeting,
        getIsRoomInCurrentWorkspace
      ),
    }),
    [actorUserId, getIsRoomInCurrentWorkspace, room, scopedPowerLevels.meeting]
  );
};

export default useMeetingPermissionsForRoom;
